import { useEffect, useMemo, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { mdiInformationOutline, mdiLoading, mdiPlusCircleOutline } from "@mdi/js";
import clsx from "clsx";
import { useToggle } from "usehooks-ts";
import * as Yup from "yup";

import Button from "components/Button";
import Checkbox from "components/Checkbox";
import Input from "components/Input";
import Select from "components/Select";
import Toggle from "components/Toggle";
import Tooltip from "components/Tooltip";
import { strings } from "constants/strings";
import AddCustomerModal from "features/main/HaulPay/modules/Shared/AddCustomerModal";
import { formatNumber } from "helpers/formatNumber";
import useDeviceType from "hooks/useDeviceType";
import { IOption } from "models/Form";
import { ICompany, IFactoringProfile, InformationFormData } from "models/HaulPay";
import { calculateFRAPI } from "services/api/haulpay";
import { selectFactoringProfile } from "store/user/selector";
import AddInvoiceDetailModal from "./modules/AddInvoiceDetailModal";
import EnterAdvanceAmountModal from "./modules/EnterAdvanceAmountModal";
import styles from "./style.module.scss";
import DebtorSearch from "components/DebtorSearchInput";
import Icon from "@mdi/react";
import debounce from "common/debounce";
import DollarInput from "components/DollarInput";

const informationFormSchema = Yup.object<Partial<InformationFormData>>({
  totalInvoiceAmount: Yup.number()
    .typeError("This field is required")
    .required("This field is required")
    .positive("Must be a positive number")
    .max(9999999.99, "Exceeds maximum amount")
    .test("max-decimals", "Only 2 decimals allowed", (value) => {
      return value ? /^\d+(\.\d{1,2})?$/.test(value.toString()) : true;
    }),
  loadNumber: Yup.string().required("This field is required"),
  advanceAmount: Yup.number()
    .nullable()
    .typeError("This field is required")
    .moreThan(0, "Advance amount must be greater than 0"),
  billToCompany: Yup.object()
    .required("This field is required")
    .shape({
      dba: Yup.string().required("Company name is required")
    }),
  fuelAdvanceAmount: Yup.number()
    .nullable()
    .when("isFuelAdvance", {
      is: true,
      then: (schema) =>
        schema
          .typeError("This field is required")
          .required("This field is required")
          .positive("Must be a positive number")
          .max(1500, "Exceeds the maximum amount of $1500")
          .test("max-decimals", "Only 2 decimals allowed", (value) => {
            return value ? /^\d+(\.\d{1,2})?$/.test(value.toString()) : false;
          })
          .test("max-pennies", "Exceeds the maximum amount of $1500", (value) => {
            return value !== undefined ? Math.round(value * 100) <= 150000 : true;
          }),
      otherwise: (schema) => schema.nullable()
    })
});

interface PayInformationProps {
  companyValue?: ICompany;
  informationFormData?: InformationFormData;
  setIsShowSearch: (show: boolean) => void;
  handleNext: (data?: InformationFormData) => Promise<void>;
  setFormDataCallback: (data: InformationFormData, isValid?: boolean) => void;
}

export default function PayInformation({
  informationFormData,
  handleNext,
  setFormDataCallback
}: Readonly<PayInformationProps>) {
  const methods = useForm<Partial<InformationFormData>>({
    resolver: yupResolver(informationFormSchema),
    defaultValues: { ...informationFormData }
  });
  const {
    register,
    setValue,
    getValues,
    handleSubmit,
    setError,
    clearErrors,
    watch,
    control,
    formState: { errors, isSubmitting }
  } = methods;
  const isNonFactored = watch("isNonFactoredInvoice");
  const totalAmount = watch("totalInvoiceAmount");
  const advanceAmount = watch("advanceAmount");
  const isFuelAdvance = watch("isFuelAdvance");
  const billToCompany = watch("billToCompany");

  const factoringProfile: IFactoringProfile = useSelector(selectFactoringProfile);
  const factoringFuelAdvance = factoringProfile?.fuel_advance;
  const [yourRate, setYourRate] = useState("0%");
  const [totalToBePaid, setTotalToBePaid] = useState(0);
  const [isDisabledFuelAdvance, setIsDisabledFuelAdvance] = useState(true);
  const [isShowNonFactoredInvoice, setIsShowNonFactoredInvoice] = useState(true);

  const [isAddCustomerModalOpen, toggleAddCustomerModal] = useToggle();
  const [isInvoiceDetailAddModalOpen, toggleInvoiceDetailAddModal] = useToggle();
  const [confirmAdvanceAmountModal, setConfirmAdvanceAmountModal] = useState({
    isOpen: false,
    handleClose: () => {},
    handleSubmit: () => {}
  });

  const { isDesktop } = useDeviceType();

  const onSubmit = async (_data: InformationFormData) => {
    const isAdvanceAmountPaid = await new Promise((resolve) =>
      setConfirmAdvanceAmountModal({
        isOpen: true,
        handleClose: () => {
          setValue("advanceAmount", undefined);
          resolve(false);
          setConfirmAdvanceAmountModal((prevState) => ({
            ...prevState,
            isOpen: false
          }));
        },
        handleSubmit: () => {
          resolve(true);
          setConfirmAdvanceAmountModal((prevState) => ({
            ...prevState,
            isOpen: false
          }));
        }
      })
    );
    setFormDataCallback(_data as InformationFormData);
    if (isAdvanceAmountPaid) {
      return await handleNext(_data as InformationFormData);
    }
    await handleNext(_data as InformationFormData);
  };
  const messageForValidBillToCompany = !getValues("billToCompany")
    ? strings.CARRIER_FR.ADD_DEBTOR_TO_FUEL_ADVANCE
    : !billToCompany?.noa_active
      ? strings.CARRIER_FR.FUEL_ADVANCE_NOA_NOT_PLACED
      : !billToCompany?.fuel_advance
        ? strings.CARRIER_FR.FUEL_ADVANCE_UNAVAILABLE
        : "";

  const paymentMethods: IOption[] = useMemo(() => {
    if (factoringProfile) {
      setValue("discountRate", factoringProfile.discount_rate);
      const sortedAccounts = [...factoringProfile.accounts].sort((a, b) => {
        if (a.method === "ACH") return -1;
        if (b.method === "ACH") return 1;
        return 0;
      });

      return sortedAccounts.map((account, index) => ({
        id: index,
        label: `${account.method} (**** ${account.account_number_redacted})`,
        value: `${account.method}${account.account_number_redacted}`
      }));
    } else {
      return [];
    }
  }, [factoringProfile]);

  const calculateFactoring = useMemo(
    () =>
      debounce(() => {
        if (billToCompany && totalAmount !== undefined) {
          calculateFRAPI({
            debtor: billToCompany?.id ?? "",
            factored: isNonFactored === undefined ? true : !isNonFactored,
            invoice_amount: (totalAmount ?? 0) * 100 - (advanceAmount ?? 0) * 100,
            create_rates_revision: false,
            payment_profile_amount: 0
          }).then((response) => {
            setTotalToBePaid(response.data.factoring_payable / 100);
            if (response.data.factored) {
              setYourRate(response.data.discount_rate_comfreight + "%");
            } else {
              setYourRate("$" + (response.data.factoring_fee / 100).toFixed(2));
            }
          });
        }
      }, 300),
    [billToCompany, totalAmount, isNonFactored, advanceAmount, calculateFRAPI]
  );

  useEffect(() => {
    return () => calculateFactoring.cancel();
  }, [calculateFactoring]);

  useEffect(() => {
    calculateFactoring();
  }, [billToCompany, totalAmount, isNonFactored, advanceAmount]);

  return factoringProfile ? (
    <FormProvider {...methods}>
      <form className={styles.payInformationContainer} onSubmit={handleSubmit(onSubmit)}>
        <div className={styles.body}>
          <h4 className={styles.header}>Information</h4>

          <div className={clsx(styles.paymentForm, "!border-none")}>
            <DollarInput
              name="totalInvoiceAmount"
              label="&nbsp;&nbsp;Total Invoice Amount"
              error={errors.totalInvoiceAmount}
            />
            <Input
              name="loadNumber"
              register={register}
              label="&nbsp;&nbsp;Load Number"
              error={errors.loadNumber}
            />
            <DebtorSearch
              name="billToCompany"
              onSelect={(company: ICompany) => {
                setValue("billToCompany", company);
                setIsDisabledFuelAdvance(
                  !factoringFuelAdvance || !company?.fuel_advance || !company?.noa_active
                );
                setIsShowNonFactoredInvoice(
                  company.credit_approved === "approved" ||
                    (company.credit_approved === "pending" && !company.noa_active)
                );
                setIsShowNonFactoredInvoice(company.credit_approved === "approved");
                setValue("isFuelAdvance", false);
                clearErrors("billToCompany");
              }}
              label="&nbsp;&nbsp;Bill To Company"
              error={errors?.billToCompany as any}
              showAddNewCustomer
            />
            <Button
              type="button"
              variant="outline"
              className={styles.paymentSubmit}
              onClick={toggleAddCustomerModal}>
              New Customer
            </Button>
          </div>

          <div className={styles.paymentDetails}>
            <div className={styles.nonFactoredInvoice}>
              <button type="button" className="text-info" onClick={toggleInvoiceDetailAddModal}>
                + Add Invoice Details
              </button>
              <button
                type="button"
                className="desktop:hidden"
                onClick={toggleInvoiceDetailAddModal}>
                <Icon path={mdiPlusCircleOutline} />
              </button>
            </div>

            {factoringProfile.fuel_advance && (
              <div className={styles.nonFactoredInvoice}>
                <Toggle
                  suffix="Is this a fuel advance?"
                  className={clsx("-ml-3", isDisabledFuelAdvance && "cursor-not-allowed")}
                  name="isFuelAdvance"
                  disabled={isDisabledFuelAdvance}
                  register={register}
                />
                {isDisabledFuelAdvance ? (
                  <Tooltip
                    position={isDesktop ? "center" : "left"}
                    message={messageForValidBillToCompany}>
                    <Icon path={mdiInformationOutline} className="flex-none text-text" />
                  </Tooltip>
                ) : null}
              </div>
            )}
            {isShowNonFactoredInvoice && (
              <div className={styles.nonFactoredInvoice}>
                <Checkbox
                  name="isNonFactoredInvoice"
                  register={register}
                  error={errors.isNonFactoredInvoice}
                  wrapperClass="text-sm desktop:text-base">
                  This is a non-factored invoice?
                </Checkbox>
                <Tooltip
                  position="left"
                  message="We will process this like usual except you will get paid after we receive the money from your customer.">
                  <Icon path={mdiInformationOutline} className="flex-none text-text" />
                </Tooltip>
              </div>
            )}
          </div>
          <div className={styles.paymentMethod}>
            {paymentMethods.length ? (
              <Controller
                name="paymentMethod"
                defaultValue={paymentMethods[0]}
                control={control}
                render={({ field: { value, onChange } }) => (
                  <Select
                    value={value}
                    options={paymentMethods}
                    onChange={onChange}
                    label="&nbsp;&nbsp;Payment Method"
                    data-cy="payment-method-select"
                  />
                )}
              />
            ) : null}
            {isFuelAdvance && (
              <DollarInput
                name="fuelAdvanceAmount"
                label="&nbsp;&nbsp;Fuel Advance Amount"
                error={errors.fuelAdvanceAmount}
              />
            )}
          </div>
          <div className="flex w-3/4">
            {billToCompany?.originals_required && (
              <p className="text-info" style={{ fontSize: "12", paddingTop: 5 }}>
                This debtor requires copies of Originals Documents to be mailed to them directly.
                <br />
                Original Documents are physical copies of your actual signed bill of lading and any
                receipts or other paperwork associated with the load. <br />
                Please mail them to the debtor directly and notify invoice@haulpay.io once you have
                sent them.
              </p>
            )}
          </div>

          <div className={styles.totalPayment}>
            <span className={styles.rate}>Your Rate: {yourRate}</span>
            <div className="flex justify-between">
              <div className="text-text">
                <span className="font-medium desktop:text-xl">Total </span>
                <span className="text-sm desktop:text-base">(To be Paid)</span>
              </div>
              <span className={styles.totalAmount}>${totalToBePaid.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <div className={styles.footer}>
          <Button type="submit" disabled={isSubmitting}>
            {" "}
            {isSubmitting ? <Icon path={mdiLoading} size={1} spin={1} /> : "NEXT"}
          </Button>
        </div>

        <AddCustomerModal
          isAddModalOpen={isAddCustomerModalOpen}
          toggleAddModal={toggleAddCustomerModal}
        />

        <AddInvoiceDetailModal
          isOpen={isInvoiceDetailAddModalOpen}
          toggleModalOpen={toggleInvoiceDetailAddModal}
          factoringProfile={factoringProfile}
        />

        <EnterAdvanceAmountModal
          isOpen={confirmAdvanceAmountModal.isOpen}
          toggleModalOpen={confirmAdvanceAmountModal.handleClose}
          totalToBePaidAmount={totalToBePaid}
          onConfirm={async () => {
            if (Number(getValues("advanceAmount")) * 2 > Number(getValues("totalInvoiceAmount"))) {
              return setError("advanceAmount", {
                message:
                  "Fuel advance amount must be less or equal to " +
                  formatNumber(Number(getValues("totalInvoiceAmount")), 2)
              });
            }
            confirmAdvanceAmountModal.handleSubmit();
          }}
          onDecline={confirmAdvanceAmountModal.handleClose}
        />
      </form>
    </FormProvider>
  ) : null;
}
